/**
 * Uploading the content sheet.
 *
 * Two steps on purpose: pick a file and see what it *would* do, then commit.
 * An import touches thousands of rows, so the preview is not a nicety —
 * a mis-mapped gender column or the wrong worksheet is only obvious once
 * you can see the numbers.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { Layout } from "../components/Layout";
import { BackOfficeNav } from "../components/BackOfficeNav";
import { Meter, MeterRow } from "../components/Meter";
import { useToast } from "../hooks/useToast";
import { contentAdmin, type ReviewCreds } from "../api/endpoints";
import { clearReviewCredentials, readReviewCredentials } from "../lib/session";
import { isCredentialFailure } from "../lib/backOffice";
import { ReviewSignIn } from "../components/ReviewSignIn";
import { useRefreshOnFocus } from "../hooks/useRefreshOnFocus";
import type { ContentStats, ImportSummary } from "../types/api";

export function AdminContent() {
  const toast = useToast();
  const [creds, setCreds] = useState<ReviewCreds | null>(() => readReviewCredentials());
  const [file, setFile] = useState<File | null>(null);
  const [sheet, setSheet] = useState("");
  const [deactivateMissing, setDeactivateMissing] = useState(false);
  const [preview, setPreview] = useState<ImportSummary | null>(null);
  const [imported, setImported] = useState<ImportSummary | null>(null);
  const [busy, setBusy] = useState<"preview" | "import" | null>(null);
  const [stats, setStats] = useState<ContentStats | null>(null);
  const [statsNonce, setStatsNonce] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!creds) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await contentAdmin.stats(creds);
        if (!cancelled) setStats(result);
      } catch (err) {
        // The pool summary is context, not the job, so a failure never blocks
        // the upload. A rejected key is different: send them to sign in.
        if (!cancelled && isCredentialFailure(err)) {
          clearReviewCredentials();
          setCreds(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [creds, statsNonce]);

  // The pool changes as contributors record, so keep the counts honest.
  // Never mid-upload, which would clear a preview they are reading.
  useRefreshOnFocus(() => setStatsNonce((value) => value + 1), {
    enabled: busy === null,
  });

  const pickFile = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setFile(event.target.files?.[0] ?? null);
    setPreview(null);
    setImported(null);
  }, []);

  async function runPreview() {
    if (!creds || !file) return;
    setBusy("preview");
    setImported(null);
    try {
      const result = await contentAdmin.importSheet(creds, file, {
        dryRun: true,
        sheet: sheet.trim() || undefined,
      });
      setPreview(result);
    } catch (err) {
      toast.show((err as Error).message, "error");
      setPreview(null);
      if (isCredentialFailure(err)) {
        clearReviewCredentials();
        setCreds(null);
      }
    } finally {
      setBusy(null);
    }
  }

  async function runImport() {
    if (!creds || !file) return;
    setBusy("import");
    try {
      const result = await contentAdmin.importSheet(creds, file, {
        dryRun: false,
        sheet: sheet.trim() || undefined,
        deactivateMissing,
      });
      setImported(result);
      setPreview(null);
      setStatsNonce((value) => value + 1);
      toast.show(
        `Imported ${result.written?.responses_added ?? 0} new responses.`,
        "success",
      );
    } catch (err) {
      toast.show((err as Error).message, "error");
    } finally {
      setBusy(null);
    }
  }

  function reset() {
    setFile(null);
    setPreview(null);
    setImported(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  if (!creds) {
    return <ReviewSignIn onSubmit={setCreds} />;
  }

  const summary = imported ?? preview;

  return (
    <Layout showNav={false} showAccount={false} title="Content">
      <section className="section">
        <div className="wrap">
          <BackOfficeNav />

          <div className="eyebrow">Content library</div>
          <h1 className="display" style={{ fontSize: "clamp(22px,4vw,32px)", margin: "0 0 18px" }}>
            Upload responses
          </h1>

          {/* Rendered before the numbers arrive, so the form below does not
              jump down the page when they do. */}
          <MeterRow className="mb-24">
            <Meter label="Clips" value={stats?.items.toLocaleString()} />
            <Meter label="Responses" value={stats?.slots_total.toLocaleString()} />
            <Meter label="Still open" value={stats?.slots_open.toLocaleString()} />
          </MeterRow>

          <div className="panel">
            <div className="card-label">The sheet</div>

            <div className="field">
              <label htmlFor="sheet-file">Spreadsheet (.xlsx or .csv)</label>
              <input
                id="sheet-file"
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xlsm,.csv"
                onChange={pickFile}
              />
              <div className="hint">
                One row per clip with <code>Response 1…4</code> columns, or one row per
                response with <code>response_number</code> and <code>response_text</code>.
              </div>
            </div>

            <div className="field">
              <label htmlFor="sheet-name">Worksheet (optional)</label>
              <input
                id="sheet-name"
                value={sheet}
                placeholder="Defaults to “Prompts” when the workbook has one"
                onChange={(event) => setSheet(event.target.value)}
              />
            </div>

            <div className="btn-row mt-16">
              <button
                type="button"
                className="btn btn-primary"
                style={{ width: "auto" }}
                disabled={!file || busy !== null}
                onClick={() => void runPreview()}
              >
                {busy === "preview" ? "Reading…" : "Preview what this does"}
              </button>
              {(file || summary) && (
                <button type="button" className="btn btn-ghost" onClick={reset} disabled={busy !== null}>
                  Clear
                </button>
              )}
            </div>
          </div>

          {summary && (
            <div className="panel mt-24">
              <div className="card-label">
                {imported ? "Imported" : "Dry run — nothing written yet"}
              </div>

              <div className="import-grid">
                <Stat label="Clips" value={summary.clips.toLocaleString()} />
                <Stat label="Responses" value={summary.responses.toLocaleString()} />
                <Stat label="Rows read" value={summary.rows_read.toLocaleString()} />
                <Stat label="Layout" value={summary.layout} />
              </div>

              <div className="import-voices">
                <span className="pill">{summary.gender_counts.male ?? 0} male</span>
                <span className="pill">{summary.gender_counts.female ?? 0} female</span>
                <span className="pill">{summary.gender_counts.any ?? 0} any voice</span>
              </div>

              {summary.sheet_names.length > 1 && (
                <div className="hint">
                  Workbook sheets: {summary.sheet_names.join(", ")} — reading{" "}
                  <b>{sheet.trim() || "Prompts"}</b>.
                </div>
              )}

              {Object.keys(summary.categories).length > 0 && (
                <div className="hint">
                  {Object.keys(summary.categories).length} categories ·{" "}
                  {Object.entries(summary.categories)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 5)
                    .map(([name, count]) => `${name} (${count})`)
                    .join(", ")}
                </div>
              )}

              {/* Things worth a second look before committing. */}
              {(summary.skipped_blank_responses > 0 ||
                summary.skipped_untitled_rows > 0 ||
                summary.legacy_recorded > 0 ||
                Object.keys(summary.unrecognised_genders).length > 0) && (
                <div className="import-warnings">
                  {summary.skipped_blank_responses > 0 && (
                    <div>{summary.skipped_blank_responses} blank responses skipped.</div>
                  )}
                  {summary.skipped_untitled_rows > 0 && (
                    <div>{summary.skipped_untitled_rows} rows had no title and were skipped.</div>
                  )}
                  {summary.legacy_recorded > 0 && (
                    <div>
                      {summary.legacy_recorded} already recorded elsewhere — imported retired, so
                      they are never handed out again.
                    </div>
                  )}
                  {Object.keys(summary.unrecognised_genders).length > 0 && (
                    <div>
                      Unrecognised voice values, treated as “any”:{" "}
                      {Object.entries(summary.unrecognised_genders)
                        .map(([label, count]) => `${label} (${count})`)
                        .join(", ")}
                    </div>
                  )}
                </div>
              )}

              {summary.sample.length > 0 && (
                <>
                  <div className="card-label mt-24">First clip, as it will be stored</div>
                  {summary.sample.slice(0, 1).map((item) => (
                    <div key={item.item_id} className="import-sample">
                      <div className="log-title">{item.title}</div>
                      <div className="log-meta muted">
                        {item.item_id} · {item.category ?? "no category"}
                      </div>
                      {item.responses.map((response) => (
                        <div key={response.number} className="import-response">
                          <span className="pill">#{response.number} {response.gender}</span>
                          <span>{response.text}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </>
              )}

              {imported?.written && (
                <div className="import-written">
                  <div>{imported.written.clips_added.toLocaleString()} clips added</div>
                  <div>{imported.written.responses_added.toLocaleString()} responses added</div>
                  <div>{imported.written.responses_updated.toLocaleString()} responses updated</div>
                  {imported.written.responses_retired > 0 && (
                    <div>{imported.written.responses_retired.toLocaleString()} retired</div>
                  )}
                </div>
              )}

              {!imported && (
                <>
                  <label className="check-row mt-24">
                    <input
                      type="checkbox"
                      checked={deactivateMissing}
                      onChange={(event) => setDeactivateMissing(event.target.checked)}
                    />
                    <span className="check-box" />
                    <span style={{ fontSize: 13 }}>
                      Retire responses that are no longer in this file. Only ones nobody has
                      claimed or recorded — work in progress is never removed.
                    </span>
                  </label>

                  <button
                    type="button"
                    className="btn btn-primary mt-16"
                    disabled={busy !== null}
                    onClick={() => void runImport()}
                  >
                    {busy === "import"
                      ? "Importing…"
                      : `Import ${summary.responses.toLocaleString()} responses`}
                  </button>
                  <div className="hint" style={{ textAlign: "center" }}>
                    Safe to re-run: wording and voice are updated in place, and a response
                    someone is already recording keeps its state.
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="k">{label}</div>
      <div className="import-stat-value">{value}</div>
    </div>
  );
}
